import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  Input,
  Avatar,
  IconButton,
  Typography,
  Tooltip,
  Button
} from "@material-tailwind/react";
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  XCircleIcon,
  EyeIcon,
  PencilSquareIcon,
  TrashIcon,
  CheckBadgeIcon
} from "@heroicons/react/24/solid";
import Swal from "sweetalert2";
import Toaster from "@/components/Toaster";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getCompanyMembers,
  setSelectedCustomer,
  getProfileUpdateRequest,
  deleteCustomer,
  approveCustomer
} from "@/store/slices/customerSlice";
import useHasPermission from "@/components/useHasPermission";

export function CompanyMemberModal({
  open,
  onClose,
  userId,
  type = "Customer",
  companyName = "",
  onRefresh
}) {
  const { t } = useTranslation("customer");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const { list_company_members, company_members_loading } = useSelector(
    (state) => state.customer
  );

  const page_url = type.replace(" ", "-")?.toLocaleLowerCase();
  const hasViewPermission = useHasPermission(`${page_url}.view`);
  const hasUpdatePermission = useHasPermission(`${page_url}.update`);
  const hasDeletePermission = useHasPermission(`${page_url}.delete`);

  useEffect(() => {
    if (open && userId) {
      dispatch(getCompanyMembers({ user_id: userId, type, search: "" }));
    }
  }, [open, userId, type, dispatch]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleSearchKeyUp = (e) => {
    if (e.key === "Enter" && search.trim()) {
      dispatch(
        getCompanyMembers({ user_id: userId, type, search: search.trim() })
      );
    } else if (e.key === "Backspace" && e.target.value.length === 0) {
      dispatch(getCompanyMembers({ user_id: userId, type, search: "" }));
    }
  };

  const handleSearchClick = () => {
    dispatch(
      getCompanyMembers({ user_id: userId, type, search: search.trim() })
    );
  };

  const handleClearSearch = () => {
    setSearch("");
    dispatch(getCompanyMembers({ user_id: userId, type, search: "" }));
  };

  const handleView = (member) => {
    if (!member?.id) return;
    dispatch(setSelectedCustomer(member));
    onClose();
    if (member.parent_id) {
      navigate(`/dashboard/customer-members/view/${member.id}`);
    } else {
      dispatch(getProfileUpdateRequest(member.id))
        .unwrap()
        .then((res) => {
          console.log("View API Data:", res);
        })
        .catch((err) => {
          console.error("View API Error:", err);
        });
      navigate(`/dashboard/${type.toLocaleLowerCase()}s/view/${member.id}`);
    }
  };

  const handleEdit = (member) => {
    if (!member?.id) return;
    dispatch(setSelectedCustomer(member));
    onClose();
    if (member.parent_id) {
      navigate(`/dashboard/customer-members/edit/${member.id}`);
    } else {
      navigate(`/dashboard/${type.toLocaleLowerCase()}s/edit/${member.id}`);
    }
  };

  const handleDelete = (member) => {
    if (!member?.id) return;
    Swal.fire({
      title: t("are_you_sure"),
      text: t("you_wont_be_able_to_revert_this"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: t("yes_delete_it"),
      customClass: {
        container: "!z-[999999]",
        popup: "!z-[999999]",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteCustomer(member.id))
          .unwrap()
          .then((res) => {
            if (res?.status !== false) {
              Toaster.success(t("deleted_successfully", "Deleted successfully!"));
              dispatch(
                getCompanyMembers({ user_id: userId, type, search: search.trim() })
              );
              if (onRefresh) onRefresh();
            } else {
              Toaster.error(res?.message || t("delete_failed"));
            }
          })
          .catch((error) => {
            console.error("Delete failed:", error);
            Toaster.error(t("delete_failed"));
          });
      }
    });
  };

  const handleApproveReject = (member, value) => {
    if (!member?.id) return;
    const msg =
      value === "approved"
        ? "approve"
        : value === "rejected"
        ? "reject"
        : value;
    const actionVerbs = ["approve", "reject"];
    let text;
    if (actionVerbs.includes(msg.toLowerCase())) {
      text = t("do_you_want_to_action_this_user", {
        action: msg,
        user: type.toLowerCase()
      });
    } else {
      text = t("do_you_want_to_make_this_user_status", {
        user: type.toLowerCase(),
        status: msg
      });
    }

    Swal.fire({
      title: t("are_you_sure"),
      text: `${text}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: t("yes"),
      customClass: {
        container: "!z-[999999]",
        popup: "!z-[999999]",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(approveCustomer({ id: member.id, value }))
          .unwrap()
          .then((res) => {
            const pastTenseMap = {
              active: "activated",
              inactive: "inactivated",
              approved: "approved",
              rejected: "rejected"
            };
            const pastTenseMsg = pastTenseMap[res.status] ?? res.status + "ed";

            Toaster.success(`${pastTenseMsg} ${t("successfully")}`);
            dispatch(
              getCompanyMembers({ user_id: userId, type, search: search.trim() })
            );
            if (onRefresh) onRefresh();
          })
          .catch((error) => {
            console.error("Changes failed:", error);
            Toaster.error(t("changes_failed"));
          });
      }
    });
  };

  const members =
    list_company_members?.data?.users ||
    list_company_members?.users ||
    [];
  const totalCount =
    list_company_members?.data?.total_users ??
    list_company_members?.total_users ??
    members.length;

  const formatGender = (gender) => {
    if (!gender) return "—";
    const lower = gender.toLowerCase();
    if (lower === "male") return t("male", "Male");
    if (lower === "female") return t("female", "Female");
    if (lower === "other") return t("other", "Other");
    return gender;
  };

  const formatDesignation = (member) => {
    if (!member?.designation) {
      return !member?.parent_id
        ? t("company_owner", "Company Owner")
        : t("member", "Member");
    }

    const designationKey = member.designation
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");

    return t(`designation_${designationKey}`, member.designation);
  };

  return (
    // <Dialog
    //   open={open}
    //   handler={onClose}
    //   size="xl"
    //   className="rounded-2xl p-0 overflow-hidden max-w-[95vw] lg:max-w-5xl"
    // >
    <Dialog
      open={open}
      handler={onClose}
      dismiss={{ outsidePress: false }}
      size="xl"
      className="rounded-2xl p-0 overflow-hidden max-w-[95vw] lg:max-w-5xl"
    >
      <DialogHeader className="flex justify-between items-center px-6 py-4 border-b bg-gray-50/70">
        <div className="flex flex-col">
          <Typography variant="h5" className="font-bold text-gray-900 flex items-center gap-2">
            <span>{t("company_members_title", "Customer Members")}</span>
            <span className="text-xs bg-blue-100 text-blue-800 font-semibold px-2.5 py-0.5 rounded-full">
              {totalCount}
            </span>
          </Typography>
          {companyName && (
            <Typography variant="small" className="text-gray-500 font-normal mt-0.5">
              {companyName}
            </Typography>
          )}
        </div>
        <IconButton
          variant="text"
          color="gray"
          onClick={onClose}
          className="p-1 rounded-full text-gray-500 hover:bg-gray-200 transition"
        >
          <XMarkIcon className="w-6 h-6" />
        </IconButton>
      </DialogHeader>

      <DialogBody className="px-4 sm:px-6 py-4 max-h-[75vh] overflow-y-auto">
        {/* Search Bar */}
        <div className="flex flex-col sm:flex-row items-center gap-2 mb-4">
          <div className="w-full relative">
            <Input
              label={t("search", "Search")}
              // placeholder={t("search_members", "Search by name, email or phone...")}
              value={search}
              onChange={handleSearchChange}
              onKeyUp={handleSearchKeyUp}
              icon={!search && <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />}
            />
            {search && (
              <IconButton
                variant="text"
                size="sm"
                className="!absolute right-1 top-1"
                onClick={handleClearSearch}
              >
                <XCircleIcon className="h-5 w-5 text-red-500" />
              </IconButton>
            )}
          </div>
          {search && (
            <Button
              size="md"
              className="w-full sm:w-auto bg-black text-white font-medium capitalize shrink-0 py-2.5 px-4"
              onClick={handleSearchClick}
            >
              {t("search", "Search")}
            </Button>
          )}
        </div>

        {/* Loading Spinner */}
        {company_members_loading ? (
          <div className="flex justify-center items-center py-16">
            <div role="status">
              <svg
                aria-hidden="true"
                className="inline w-9 h-9 text-gray-200 animate-spin fill-blue-600"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
            </div>
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-12 text-gray-500 font-medium">
            {t("no_members_found", "No members found.")}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
            <table className="w-full min-w-[760px] table-auto text-left">
              <thead>
                <tr className="bg-gray-100 border-b border-gray-200">
                  <th className="py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {t("full_name", "Full Name")}
                  </th>
                  <th className="py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {t("email", "Email")}
                  </th>
                  <th className="py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {t("mobile_number", "Mobile Number")}
                  </th>
                  <th className="py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {t("gender", "Gender")}
                  </th>
                  <th className="py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {t("designation", "Designation")}
                  </th>
                  <th className="py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    {t("status", "Status")}
                  </th>
                  <th className="py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider text-center">
                    {t("actions", "Actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {members.map((member) => {
                  const fullName = [member.first_name, member.last_name]
                    .filter(Boolean)
                    .join(" ") || "—";
                  const phoneFormatted = member.mobile_number
                    ? `${member.country_code ? member.country_code + " " : ""}${member.mobile_number}`
                    : "—";

                  return (
                    <tr
                      key={member.id}
                      className="hover:bg-blue-50/40 transition-colors"
                    >
                      {/* Avatar + Full Name */}
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <Avatar
                            src={
                              member.profile_image ||
                              "/img/default-avatar-profile-icon.avif"
                            }
                            alt={fullName}
                            size="sm"
                            variant="circular"
                            className="border border-gray-200 shadow-sm shrink-0"
                            onError={(e) => {
                              e.target.src =
                                "/img/default-avatar-profile-icon.avif";
                            }}
                          />
                          <div className="flex flex-col">
                            <Typography
                              variant="small"
                              className="font-semibold text-gray-900"
                            >
                              {fullName}
                            </Typography>
                          </div>
                        </div>
                      </td>

                      {/* Email */}
                      <td className="py-3 px-4">
                        <Typography
                          variant="small"
                          className="text-gray-700 font-normal break-all"
                        >
                          {member.email || "—"}
                        </Typography>
                      </td>

                      {/* Mobile */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        <Typography
                          variant="small"
                          className="text-gray-700 font-normal"
                        >
                          {phoneFormatted}
                        </Typography>
                      </td>

                      {/* Gender */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        <Typography
                          variant="small"
                          className="text-gray-700 font-normal"
                        >
                          {formatGender(member.gender)}
                        </Typography>
                      </td>

                      {/* Designation */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            !member.parent_id
                              ? "bg-purple-50 text-purple-700 border border-purple-200"
                              : "bg-blue-50 text-blue-700 border border-blue-200"
                          }`}
                        >
                          {formatDesignation(member)}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center rounded-xl px-2.5 py-1 text-xs font-medium ${
                            member.status === "pending"
                              ? "bg-yellow-50 text-yellow-800"
                              : member.status === "active"
                              ? "bg-green-50 text-green-700"
                              : "bg-red-50 text-red-700"
                          }`}
                        >
                          {member.status
                            ? member.status.charAt(0).toUpperCase() +
                              member.status.slice(1).toLowerCase()
                            : "—"}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="py-3 px-4 text-center whitespace-nowrap">
                        <div className="flex items-center justify-center gap-1">
                          {hasViewPermission && (
                            // <Tooltip content={t("view_member", "View Member")}>
                              <IconButton
                                variant="text"
                                color="blue"
                                size="sm"
                                onClick={() => handleView(member)}
                                className="hover:bg-blue-50"
                                title={t("view_member", "View Member")}
                              >
                                <EyeIcon className="h-5 w-5 text-blue-500" />
                              </IconButton>
                            // </Tooltip>
                          )}

                          {hasUpdatePermission && (
                            // <Tooltip content={t("edit_member", "Edit Member")}>
                              <IconButton
                                variant="text"
                                color="blue"
                                size="sm"
                                onClick={() => handleEdit(member)}
                                className="hover:bg-blue-50"
                                title={t("edit_member", "Edit Member")}
                              >
                                <PencilSquareIcon className="h-5 w-5 text-blue-500" />
                              </IconButton>
                            // </Tooltip>
                          )}

                          {hasUpdatePermission && (
                            <>
                              {member.status === "active" && (
                                // <Tooltip content={t("inactivate", "Inactivate")}>
                                  <IconButton
                                    variant="text"
                                    color="blue"
                                    size="sm"
                                    onClick={() =>
                                      handleApproveReject(member, "inactive")
                                    }
                                    className="hover:bg-blue-50"
                                    title={t("inactivate", "Inactivate")}
                                  >
                                    <XCircleIcon className="h-5 w-5 text-blue-500" />
                                  </IconButton>
                                // </Tooltip>
                              )}

                              {member.status === "inactive" && (
                                // <Tooltip content={t("activate", "Activate")}>
                                  <IconButton
                                    variant="text"
                                    color="blue"
                                    size="sm"
                                    onClick={() =>
                                      handleApproveReject(member, "active")
                                    }
                                    className="hover:bg-blue-50"
                                    title={t("activate", "Activate")}
                                  >
                                    <CheckBadgeIcon className="h-5 w-5 text-blue-500" />
                                  </IconButton>
                                // </Tooltip>
                              )}

                              {member.status === "pending" && (
                                <>
                                  <Tooltip content={t("approve", "Approve")}>
                                    <IconButton
                                      variant="text"
                                      color="blue"
                                      size="sm"
                                      onClick={() =>
                                        handleApproveReject(member, "approved")
                                      }
                                      className="hover:bg-blue-50"
                                    >
                                      <CheckBadgeIcon className="h-5 w-5 text-blue-500" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip content={t("reject", "Reject")}>
                                    <IconButton
                                      variant="text"
                                      color="red"
                                      size="sm"
                                      onClick={() =>
                                        handleApproveReject(member, "rejected")
                                      }
                                      className="hover:bg-red-50"
                                    >
                                      <XCircleIcon className="h-5 w-5 text-red-500" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}

                              {member.status === "rejected" && (
                                <Tooltip content={t("approve", "Approve")}>
                                  <IconButton
                                    variant="text"
                                    color="blue"
                                    size="sm"
                                    onClick={() =>
                                      handleApproveReject(member, "approved")
                                    }
                                    className="hover:bg-blue-50"
                                  >
                                    <CheckBadgeIcon className="h-5 w-5 text-blue-500" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          )}

                          {hasDeletePermission && (
                            // <Tooltip content={t("delete", "Delete")}>
                              <IconButton
                                variant="text"
                                color="red"
                                size="sm"
                                onClick={() => handleDelete(member)}
                                className="hover:bg-red-50"
                                title={t("delete", "Delete")}
                              >
                                <TrashIcon className="h-5 w-5 text-red-500" />
                              </IconButton>
                            // </Tooltip>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </DialogBody>
    </Dialog>
  );
}

export default CompanyMemberModal;
